import {
  type KeyValueResp,
  MetadataKey,
  type User,
} from '@revelio/data-access';

import type { UserOverrides } from './impersonation.model';

export const mergeMetadataOverrides = (
  metadata: Array<KeyValueResp | null> | null | undefined,
  updates: Array<{ key: MetadataKey; value: string }>
): KeyValueResp[] => {
  const metadataValues = (metadata ?? []).filter((item): item is KeyValueResp =>
    Boolean(item?.key)
  );
  const updateMap = new Map(updates.map((item) => [item.key, item.value]));

  const mergedMetadata = metadataValues.map((item) =>
    item.key && updateMap.has(item.key)
      ? { ...item, value: updateMap.get(item.key) }
      : item
  );

  updates.forEach((update) => {
    const exists = metadataValues.some((item) => item.key === update.key);
    if (!exists) {
      mergedMetadata.push({ key: update.key, value: update.value });
    }
  });

  return mergedMetadata;
};

export const applyOverridesToUser = (
  user: User,
  overrides: UserOverrides
): User => {
  const updatedUser = { ...user };

  if (overrides.isTrialUser !== undefined) {
    updatedUser.live = !overrides.isTrialUser;
  } else if (overrides.live !== undefined) {
    updatedUser.live = overrides.live;
  }

  if (overrides.clientName !== undefined) {
    updatedUser.client_name = overrides.clientName;
  }

  if (overrides.role !== undefined) {
    updatedUser.role = overrides.role;
  }

  if (overrides.tabs !== undefined) {
    updatedUser.tabs = overrides.tabs;
  }

  if (overrides.linkupPostings !== undefined) {
    updatedUser.linkup_postings = overrides.linkupPostings;
  }

  const hasMetadataOverrides =
    overrides.trialType !== undefined ||
    overrides.expirationDate !== undefined ||
    overrides.tourCompleted !== undefined ||
    overrides.stepsCompleted !== undefined;

  if (hasMetadataOverrides) {
    const metadataUpdates: Array<{ key: MetadataKey; value: string }> = [];

    if (overrides.trialType !== undefined) {
      metadataUpdates.push({
        key: MetadataKey.TrialType,
        value: overrides.trialType,
      });
    }

    if (overrides.expirationDate !== undefined) {
      metadataUpdates.push({
        key: MetadataKey.ExpirationDate,
        value: overrides.expirationDate,
      });
    }

    if (overrides.tourCompleted !== undefined) {
      metadataUpdates.push({
        key: MetadataKey.TourCompleted,
        value: String(overrides.tourCompleted),
      });
    }

    if (overrides.stepsCompleted !== undefined) {
      metadataUpdates.push({
        key: MetadataKey.StepsCompleted,
        value: overrides.stepsCompleted,
      });
    }

    updatedUser.metadata = mergeMetadataOverrides(
      updatedUser.metadata,
      metadataUpdates
    );
  }

  return updatedUser;
};

export const applyImpersonatedUser = (
  user: User,
  impersonatedUser: Partial<User>
): User => {
  const metadata =
    impersonatedUser.metadata !== undefined
      ? impersonatedUser.metadata
      : user.metadata;

  return {
    ...user,
    ...impersonatedUser,
    metadata,
    // Keep the acting admin's identity: impersonation changes what the user
    // SEES (role, tabs, trial, client), never who they ARE. Without this,
    // mutations keyed on loggedInUser.id write to the impersonated account.
    id: user.id,
  };
};
